import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { IntlProvider } from "react-intl";
import { getLocalStorage, setLocalStorage, languageStorage } from "../helpers/localStorage";
import {
  SUPPORTED_LANGUAGES,
  DEFAULT_LANGUAGE,
  FALLBACK_LANGUAGE,
  getLanguageByCode,
  isLanguageSupported,
  getBrowserLanguage,
  getLanguageFromURL
} from "../config/languages";

export const LanguageContext = createContext({});

const getInitialLanguage = () => {
  const storedLanguage = getLocalStorage(languageStorage);
  
  if (storedLanguage && isLanguageSupported(storedLanguage)) {
    return storedLanguage;
  }
  
  const urlLanguage = getLanguageFromURL(window.location.pathname);
  if (urlLanguage) {
    return urlLanguage;
  }
  
  return getBrowserLanguage();
};

export const LanguageContextProvider = ({ children }) => {
  const [language, setLanguage] = useState(getInitialLanguage);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  const currentLanguageConfig = getLanguageByCode(language);

  const changeLanguage = useCallback(async (newLanguage) => {
    if (!isLanguageSupported(newLanguage) || newLanguage === language) {
      return false;
    }

    setIsLoading(true);
    
    try {
      setLocalStorage(languageStorage, newLanguage);
      setLanguage(newLanguage);
      
      return true;
    } catch (error) {
      console.error('Error changing language:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [language]);

  const updateLanguage = useCallback(() => {
    const storedLanguage = getLocalStorage(languageStorage);
    
    if (storedLanguage && isLanguageSupported(storedLanguage) && storedLanguage !== language) {
      setLanguage(storedLanguage);
    }
  }, [language]);

  const resetToDefault = useCallback(() => {
    changeLanguage(DEFAULT_LANGUAGE);
  }, [changeLanguage]);

  useEffect(() => {
    if (!isInitialized) {
      const initialLang = getInitialLanguage();
      if (initialLang !== language) {
        setLanguage(initialLang);
      }
      setIsInitialized(true);
    }
  }, [language, isInitialized]);

  useEffect(() => {
    updateLanguage();
  }, [updateLanguage]);

  const value = {
    language,
    config: currentLanguageConfig,
    isLoading,
    isInitialized,
    supportedLanguages: SUPPORTED_LANGUAGES,
    changeLanguage,
    updateLanguage,
    resetToDefault,
    isLanguageSupported,
  };

  return (
    <LanguageContext.Provider value={value}>
      <IntlProvider
        messages={currentLanguageConfig.messages}
        locale={currentLanguageConfig.code}
        defaultLocale={FALLBACK_LANGUAGE}
        onError={(err) => {
          if (err.code === 'MISSING_TRANSLATION') {
            console.warn(`Missing translation for key: ${err.id}`);
          }
        }}
      >
        {children}
      </IntlProvider>
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageContextProvider');
  }
  
  return context;
};